import React, { useEffect, useRef, useState } from 'react';
import { FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { FaXTwitter } from "react-icons/fa6";

const australianStates = [
  "Australian Capital Territory",
  "New South Wales",
  "Northern Territory",
  "Queensland",
  "South Australia",
  "Tasmania",
  "Victoria",
  "Western Australia",
];

const useReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.1 });

    if (currentRef) observer.observe(currentRef);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const Contact = () => {
  const getformEndpoint = 'https://getform.io/f/d8dded64-137e-47cb-91b1-8dc4b18f6f30'; 

  const [headerRef, headerVisible] = useReveal();
  const [formRef, formVisible] = useReveal();
  const [sidebarRef, sidebarVisible] = useReveal();

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 my-8">
      <div 
        ref={headerRef}
        className={`transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      >
        <h1 className="text-3xl text-[#ff8400] leading-tight mb-2">We would love to connect!</h1>
        <h2 className="text-3xl text-[#703aa4] leading-tight mb-8">Drop us a line</h2>
      </div>

      <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
        <form 
          ref={formRef}
          action={getformEndpoint} 
          method="POST" 
          className={`w-full md:w-2/3 space-y-6 transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div 
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-500 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
            style={{ transitionDelay: formVisible ? '100ms' : '0ms' }}
          >
            <div className="w-full sm:w-1/2">
              <label htmlFor="firstName" className="block text-base font-medium text-gray-700 mb-2">First name</label>
              <input type="text" id="firstName" name="first-name" required maxLength={50}
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-[#703aa4] focus:ring focus:ring-[#703aa4] focus:ring-opacity-50 text-lg py-2 px-3"
              />
            </div>
            <div className="w-full sm:w-1/2">
              <label htmlFor="lastName" className="block text-base font-medium text-gray-700 mb-2">Last name</label>
              <input type="text" id="lastName" name="last-name" required maxLength={50}
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-[#703aa4] focus:ring focus:ring-[#703aa4] focus:ring-opacity-50 text-lg py-2 px-3"
              />
            </div>
          </div>

          <div 
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-500 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
            style={{ transitionDelay: formVisible ? '200ms' : '0ms' }}
          >
            <div className="w-full sm:w-1/2">
              <label htmlFor="email" className="block text-base font-medium text-gray-700 mb-2">Email</label>
              <input type="email" id="email" name="email" required maxLength={254}
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-[#703aa4] focus:ring focus:ring-[#703aa4] focus:ring-opacity-50 text-lg py-2 px-3"
              />
            </div>
            <div className="w-full sm:w-1/2">
              <label htmlFor="phone" className="block text-base font-medium text-gray-700 mb-2">Phone</label>
              <input type="tel" id="phone" name="phone" maxLength={30}
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-[#703aa4] focus:ring focus:ring-[#703aa4] focus:ring-opacity-50 text-lg py-2 px-3"
              />
            </div>
          </div>

          <div 
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-500 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
            style={{ transitionDelay: formVisible ? '300ms' : '0ms' }}
          >
            <div className="w-full sm:w-1/2">
              <label htmlFor="state" className="block text-base font-medium text-gray-700 mb-2">State</label>
              <select id="state" name="state"
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-[#703aa4] focus:ring focus:ring-[#703aa4] focus:ring-opacity-50 text-lg py-2 px-3"
              >
                {australianStates.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div className="w-full sm:w-1/2">
              <label htmlFor="postcode" className="block text-base font-medium text-gray-700 mb-2">Postal code</label>
              <input type="text" id="postcode" name="postcode" maxLength={12}
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-[#703aa4] focus:ring focus:ring-[#703aa4] focus:ring-opacity-50 text-lg py-2 px-3"
              />
            </div>
          </div>

          <div 
            className={`transition-all duration-500 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
            style={{ transitionDelay: formVisible ? '400ms' : '0ms' }}
          >
            <label htmlFor="message" className="block text-base font-medium text-gray-700 mb-2">Message</label>
            <textarea id="message" name="message" rows="4" maxLength={500}
              className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-[#703aa4] focus:ring focus:ring-[#703aa4] focus:ring-opacity-50 text-lg py-2 px-3 resize-y max-h-64"
            ></textarea>
          </div>

          {/* Disclaimer Section */}
          <div 
            className={`text-sm text-gray-600 transition-all duration-500 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: formVisible ? '500ms' : '0ms' }}
          >
            <p>
              * By completing this form you confirm that you have read and agree to the terms of the <a href="/media/Data Protection Policy-08a334ec859e764554c51a30074c3eca.pdf" target="_blank" rel="noreferrer" className="text-[#703aa4] underline">Data Privacy Policy</a>.
            </p>
          </div>

          <div 
            className={`transition-all duration-500 ${formVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
            style={{ transitionDelay: formVisible ? '600ms' : '0ms' }}
          >
            <button type="submit"
              className="bg-[#703aa4] text-white text-lg px-6 py-2 rounded-md hover:bg-opacity-90 transition duration-300"
            >
              Submit
            </button>
          </div>
        </form>

        <div 
          ref={sidebarRef}
          className={`w-full md:w-1/3 md:self-start transition-all duration-700 ${sidebarVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}
          style={{ transitionDelay: '300ms' }}
        >
          <div className="bg-[#703aa4] text-white p-6 flex flex-col justify-between h-auto">
            <div className={`transition-all duration-500 ${sidebarVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: sidebarVisible ? '400ms' : '0ms' }}
            >
              <h3 className="text-xl font-semibold leading-tight mb-4">Australia</h3>
              <p className="text-xl mb-4">1300 144 990</p>
            </div>
            <div className={`border-t border-white opacity-30 my-2 transition-all duration-500 ${sidebarVisible ? 'opacity-30 scale-x-100' : 'opacity-0 scale-x-0'}`}
              aria-hidden="true"
              style={{ transitionDelay: sidebarVisible ? '500ms' : '0ms' }}
            ></div>
            <div className={`flex space-x-4 mt-4 transition-all duration-500 ${sidebarVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: sidebarVisible ? '600ms' : '0ms' }}
            >
              <a href="https://www.facebook.com/EnergyDemocracyCoOperatives/" target="_blank" rel="noopener noreferrer" aria-label="Energy Democracy on Facebook" className="text-white hover:text-[#ff8400] transition-transform hover:scale-110"><FaFacebookF /></a>
              <a href="https://www.instagram.com/ed_co_op/" target="_blank" rel="noopener noreferrer" aria-label="Energy Democracy on Instagram" className="text-white hover:text-[#ff8400] transition-transform hover:scale-110"><FaInstagram /></a>
              <a href="https://www.linkedin.com/company/energy-democracy/" target="_blank" rel="noopener noreferrer" aria-label="Energy Democracy on LinkedIn" className="text-white hover:text-[#ff8400] transition-transform hover:scale-110"><FaLinkedinIn /></a>
              <a href="https://x.com/ed_co_op" target="_blank" rel="noopener noreferrer" aria-label="Energy Democracy on X" className="text-white hover:text-[#ff8400] transition-transform hover:scale-110"><FaXTwitter /></a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
